import axios from "axios";
import { jwtDecode } from 'jwt-decode';
import API_CONFIG from '../config/apiConfig';

// Use centralized configuration
const SERVER_URL = API_CONFIG.BASE_URL;

const instance = axios.create({ baseURL: SERVER_URL, withCredentials: true });

// Dedicated client for token refresh calls.
// Important: do NOT use `instance` here, otherwise the request interceptor can recurse.
const refreshClient = axios.create({ baseURL: SERVER_URL, withCredentials: true });

// Helper to read a cookie value by name (Phase 2 CSRF support)
function getCookie(name) {
	if (typeof document === 'undefined') return null;
	const match = document.cookie.split('; ').find(row => row.startsWith(name + '='));
	return match ? decodeURIComponent(match.split('=')[1]) : null;
}

// Attach CSRF token for unsafe methods if available
instance.interceptors.request.use(config => {
	const method = (config.method || '').toLowerCase();
	if (['post','put','patch','delete'].includes(method)) {
		if (!config.headers['X-CSRFToken']) {
			const csrf = getCookie('csrftoken');
			if (csrf) config.headers['X-CSRFToken'] = csrf;
		}
	}
	return config;
});

// Mirror CSRF behavior for refresh calls (cookie-refresh mode can require CSRF).
refreshClient.interceptors.request.use(config => {
  const method = (config.method || '').toLowerCase();
  if (['post','put','patch','delete'].includes(method)) {
    if (!config.headers['X-CSRFToken']) {
      const csrf = getCookie('csrftoken');
      if (csrf) config.headers['X-CSRFToken'] = csrf;
    }
  }
  return config;
});

// ========== TOKEN ROTATION LOGIC ==========
const USER_DETAILS_KEY = 'userDetails';
let inMemoryAccess = null;
let inFlightRefresh = null;

function getStoredDetails() {
  try {
    const raw = localStorage.getItem(USER_DETAILS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function persist(details) {
  localStorage.setItem(USER_DETAILS_KEY, JSON.stringify(details));
}

function getAccessToken() {
  if (inMemoryAccess) return inMemoryAccess;
  const d = getStoredDetails();
  if (d && d.access_token) inMemoryAccess = d.access_token;
  return inMemoryAccess;
}

function getRefreshToken() {
  const d = getStoredDetails();
  return d && d.refresh_token ? d.refresh_token : null;
}

function isExpiringSoon(token, thresholdSeconds = 120) {
  if (!token) return false;
  try {
    const decoded = jwtDecode(token);
    if (!decoded.exp) return false;
    const now = Date.now() / 1000;
    return decoded.exp - now < thresholdSeconds;
  } catch {
    return false;
  }
}

async function refreshAccessToken() {
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async () => {
    const refresh = getRefreshToken();
    if (!refresh) {
      localStorage.removeItem(USER_DETAILS_KEY);
      inMemoryAccess = null;
      throw new Error('No refresh token available');
    }

    try {
	  // Backend route is `token/refresh` (no trailing slash). A trailing slash causes 404 and breaks rotation.
	  const res = await refreshClient.post('token/refresh', { refresh });
      const newAccess = res.data.access;
      if (newAccess) {
        const details = getStoredDetails() || {};
        details.access_token = newAccess;
        if (res.data.refresh) details.refresh_token = res.data.refresh;
        persist(details);
        inMemoryAccess = newAccess;
      }
      return inMemoryAccess;
    } catch (err) {
      localStorage.removeItem(USER_DETAILS_KEY);
      inMemoryAccess = null;
      throw err;
    }
  })().finally(() => { inFlightRefresh = null; });
  
  return inFlightRefresh;
}

// Request interceptor: attach token & refresh proactively if expiring soon
instance.interceptors.request.use(async (config) => {
  const token = getAccessToken();
  if (token) {
    if (isExpiringSoon(token)) {
      try { 
        await refreshAccessToken(); 
      } catch { 
        // If refresh fails, let the request proceed with old token
        // Response interceptor will handle 401
      }
    }
    config.headers = config.headers || {};
    config.headers['Authorization'] = 'Bearer ' + getAccessToken();
  }
  return config;
});

// Response interceptor: handle 401 by refreshing token and retrying
instance.interceptors.response.use(
  response => response,
  async error => {
    const original = error.config;
    if (!original || original._retry) return Promise.reject(error);
    
    if (error.response && error.response.status === 401) {
      const data = error.response.data || {};
      const hasStoredRefresh = !!getRefreshToken();
      
      // Detect if this is a token expiry issue
      const probableExpiry = data.isTokenExpired === true ||
        (data.code === 'token_not_valid') ||
        (data.message && /login again/i.test(data.message)) ||
        hasStoredRefresh;
      
      if (probableExpiry) {
        original._retry = true;
        try {
          await refreshAccessToken();
          original.headers['Authorization'] = 'Bearer ' + getAccessToken();
          return instance(original);
        } catch (e) {
          // Refresh failed - redirect to login
          window.location.href = '/login';
          return Promise.reject(error);
        }
      }
    }
    return Promise.reject(error);
  }
);

export default instance;
